import { css } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/components/Fortune/exchangeHistory/helper';
import { AspectRatioImage } from 'src/components/shared/AspectRatioImage';
import { Construction } from 'src/models/resort/Construction';
import { UserColor } from 'src/models/resort/UserColor';
import { UserResortProfile } from 'src/models/resort/UserResortProfile';
import { PromotionPageLayout } from '../PromotionPageLayout';
import { getPhotoLineFrame } from './getPhotoLineFrame';
import { ImageHeaderFacility } from './ImageHeaderFacility';
import { ImageHeaderHouse } from './ImageHeaderHouse';
import { PlayerInfo } from './PlayerInfo';
import { StructureFacilityInfo } from './StructureFacilityInfo';
import { StructureHouseInfo } from './StructureHouseInfo';

interface MyConstructionDetailContentProps {
  isMyConstruction: true;
  construction: Construction;
  userColor: UserColor;
  decoId?: number;
}

interface OtherConstructionDetailContentProps {
  isMyConstruction: false;
  construction: Construction;
  userColor: UserColor;
  profile: UserResortProfile; // 他人のプロフィール情報
  isFollowing: boolean;
  decoId?: number;
}
type ConstructionDetailContentProps =
  | MyConstructionDetailContentProps
  | OtherConstructionDetailContentProps;

export const ConstructionDetailContent: React.FC<ConstructionDetailContentProps> = props => {
  const { isMyConstruction, construction, userColor, decoId } = props;
  const { t } = useTranslation(['resort', 'translation']);

  const constructionMainImageUrl = construction.resortImages[0];

  let imageHeader: JSX.Element | null;
  let structureInfo: JSX.Element | null;

  switch (construction.structureKind) {
    case 0:
      imageHeader = (
        <ImageHeaderHouse
          freeTheme={construction.freeTheme}
          npcs={construction.npcs}
          color={userColor}
        />
      );

      structureInfo = (
        <StructureHouseInfo
          gardenId={construction.gardenId}
          gardenImage={construction.gardenImage}
          roomNum={construction.roomNum}
          season={construction.season}
        />
      );
      break;
    case 1:
      imageHeader = (
        <ImageHeaderFacility
          freeTheme={construction.freeTheme}
          facilityId={construction.facilityId}
          color={userColor}
        />
      );

      structureInfo = (
        <StructureFacilityInfo resortImages={construction.resortImages} npcs={construction.npcs} />
      );
      break;
    default:
      structureInfo = null;
      imageHeader = null;
  }

  const photoLineFrame = getPhotoLineFrame(userColor.line);
  const dateString = formatDate(t, construction.createdAt);

  return (
    <PromotionPageLayout
      title={isMyConstruction ? t('my_resort_detail') : t('resort_detail')}
      userColor={userColor}
      decoId={decoId}
      backLabel={t('translation:aria_button_back_to_previous')}
    >
      <div
        className={css`
          max-width: 480px;
          margin: 0 auto;
          padding: 24px 20px 40px;

          .image-header {
            position: relative;
            transform: translateY(8px);
            z-index: 1;
          }
          .construction-image {
            height: auto;
          }
          .construction-image-frame {
            position: relative;
            margin: 0 8px;
            &::after {
              content: '';
              position: absolute;
              top: -18px;
              left: -18px;
              right: -18px;
              bottom: -18px;
              border: 20px solid;
              border-image-slice: 18;
              border-image-width: 20px;
              border-image-source: url('${photoLineFrame}');
            }
          }
          .created-date {
            width: max-content;
            color: ${userColor.line};
            font-size: 12px;
            margin: 14px 4px 24px auto;
          }
          .player-info {
            margin: 0 auto;
          }
          .section-label {
            font-size: 12px;
            color: #4d3906;
            margin: 32px 0 8px;
          }
        `}
      >
        <div className="image-header">{imageHeader}</div>
        <div className="construction-image-frame">
          <div
            role="img"
            className="construction-image"
            aria-label={t('aria_construction_image_00')}
          >
            <AspectRatioImage src={constructionMainImageUrl} width={960} height={540} />
          </div>
        </div>
        <p className="created-date">{dateString}</p>

        {structureInfo}

        {!isMyConstruction ? (
          <div className="player-info">
            <p className="section-label">{t('posted_by')}</p>
            <PlayerInfo profile={props.profile} isFollowing={props.isFollowing} />
          </div>
        ) : null}
      </div>
    </PromotionPageLayout>
  );
};
